import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CCol, CRow, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CSpinner,
  CCard, CCardHeader, CCardBody,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilDollar, cilFile, cilPencil,
  cilArrowLeft, cilPen, cilTrash, cilPrint, cilWarning,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import { usePendiente } from '../components/ww/pendiente'
import ModificarAbonoModal from '../components/abonos/ModificarAbonoModal'

const modalidadTexto = (m: any): string => {
  const textos: Record<number, string> = { 1: 'Diario', 7: 'Semanal', 15: 'Quincenal', 30: 'Mensual', 60: 'Pago Libre' }
  return textos[Number(m)] ?? (m === null || m === undefined || m === '' ? '—' : String(m))
}

// Moneda: fuente única utils/moneda.ts (fmtMoneda — símbolo configurado por
// empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99", aticket.cs:222). [RGM] 2026-10-02 (F)

const AbonoViewPage: React.FC = () => {
  const { nro } = useParams<{ nro: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [abono, setAbono] = useState<any>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [eliminarAbierto, setEliminarAbierto] = useState(false)
  // [RGM] 2026-10-02 (pedido Rafael — "Modificar no funciona"): modal de
  // edición del abono (PUT /api/abonos/{nro} con el contrato del form web GX).
  // [RGM] 2026-10-03: el formulario se extrajo al componente compartido
  // ModificarAbonoModal (mismo que usa la grilla AbonosPage).
  const [modAbierto, setModAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()

  const cargar = () => {
    setCargando(true)
    setError('')
    api.get<any[]>(`/abonos?empresa=${empresa}`)
      .then((lista) => {
        const encontrado = (lista ?? []).find((a) => String(a.abonoNro ?? '') === String(nro ?? ''))
        if (!encontrado) setError('Abono no encontrado')
        setAbono(encontrado ?? null)
      })
      .catch((e: any) => setError(e.message ?? 'Abono no encontrado'))
      .finally(() => setCargando(false))
  }

  useEffect(() => { cargar() }, [nro, empresa]) // eslint-disable-line react-hooks/exhaustive-deps

  const v = (valor: any): string => (valor === null || valor === undefined || valor === '' ? '—' : String(valor))

  const fila = (label: string, valor: any) => (
    <CCol md={6}>
      <div className="small text-body-secondary">{label}</div>
      <div className="fw-medium text-break">{valor ?? '—'}</div>
    </CCol>
  )

  const tarjeta = (icono: any, titulo: string, contenido: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className=" d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
        <CIcon icon={icono} size="sm" /> {titulo}
      </CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{contenido}</CRow>
      </CCardBody>
    </CCard>
  )

  // [RGM] 2026-10-02 (pedido Rafael — "Modificar no funciona"): abre el modal
  // compartido con los campos editables del form web GX (abono.cs): Valor,
  // Fecha (admin), Cuotas, Próximo Cobro y campos libres. El PUT
  // (AbonosController.Modificar — abono_bc.cs UPD: saldo, acumuladores y caja)
  // ya existe en el backend.
  const abrirModificar = () => {
    setError('')
    setModAbierto(true)
  }

  const confirmarEliminar = () => {
    setEliminarAbierto(false)
    setAviso('La eliminación está pendiente de réplica (no existe endpoint DELETE en el backend)')
  }

  // [RGM] 2026-10-02 (fix 401): el window.open directo a /api abría la pestaña
  // SIN el token de sesión (401). [RGM] 2026-10-03: el patrón se movió al
  // helper global api.abrirPdf (pestaña nueva con blob autenticado, anti-popup
  // — lo comparten TODOS los PDFs).
  const abrirTicket = async () => {
    try {
      await api.abrirPdf(`/abonos/${nro}/ticket.pdf?empresa=${empresa}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el ticket')
    }
  }

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (error && !abono) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Abono</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/abonos')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">Abono no encontrado</CAlert>
      </>
    )
  }

  return (
    <>
      <CRow className="mb-4">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h4 className="mb-0">Abono {nro}</h4>
          </div>
          <div className="d-flex gap-2">
            {/* Ticket PDF: se ABRE EN EL NAVEGADOR (réplica aticket.cs; la web
                no envía WhatsApp — el ticket se imprime/comparte del PDF).
                [RGM] 2026-10-02: vía cliente API con token (window.open directo
                a /api daba 401). */}
            <CButton color="primary" variant="outline" onClick={abrirTicket}>
              <CIcon icon={cilPrint} className="me-1" size="sm" />
              Ticket PDF
            </CButton>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/abonos')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
            <CButton color="primary" onClick={abrirModificar}>
              <CIcon icon={cilPen} className="me-1" size="sm" />
              Modificar
            </CButton>
            <CButton color="danger" onClick={() => setEliminarAbierto(true)}>
              <CIcon icon={cilTrash} className="me-1" size="sm" />
              Eliminar
            </CButton>
          </div>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      {tarjeta(cilDollar, 'Abono', (
        <>
          {fila('Nro', abono?.abonoNro)}
          {fila('Fecha', v(abono?.abonoFecha).slice(0, 10))}
          {fila('Hora', abono?.abonoHora)}
          {fila('Tipo Abono', abono?.abonoTipoAbono)}
          {fila('Valor', fmtMoneda(abono?.abonoValor))}
          {fila('Cuotas', abono?.abonoCuotas)}
          {fila('Próximo Cobro', v(abono?.abonoProximoCobro).slice(0, 10))}
          {fila('Creado Por', abono?.abonoCreadoPor)}
        </>
      ))}

      {tarjeta(cilFile, 'Préstamo', (
        <>
          {fila('Nro', abono?.prestamoNro)}
          {fila('Fecha', v(abono?.prestamoFecha).slice(0, 10))}
          {fila('Saldo', fmtMoneda(abono?.prestamoSaldo))}
          {fila('Cuota', fmtMoneda(abono?.prestamoCuota))}
          {fila('Modalidad', modalidadTexto(abono?.prestamoMPago))}
          {fila('Cédula', abono?.clienteCedula)}
          {fila('Nombre', abono?.clienteNombre)}
          {fila('WhatsApp', abono?.clienteCelular)}
        </>
      ))}

      {tarjeta(cilPencil, 'Observación', (
        <>
          <CCol md={12}>
            <div className="small text-body-secondary">Observación</div>
            <div className="fw-medium text-break">{v(abono?.abonoLibreCaracter1)}</div>
          </CCol>
        </>
      ))}

      {/* [RGM] 2026-10-02 (pedido Rafael — "Modificar no funciona"): modal
          compartido (mismo formulario que la grilla de AbonosPage). */}
      <ModificarAbonoModal
        abono={abono}
        empresa={empresa}
        visible={modAbierto}
        onClose={() => setModAbierto(false)}
        onSaved={() => {
          setModAbierto(false)
          setAviso('Abono modificado.')
          cargar()
        }}
      />

      <CModal visible={eliminarAbierto} onClose={() => setEliminarAbierto(false)}>
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilWarning} className="me-1 text-danger" />
            Eliminar abono
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="mb-0">
            ¿Eliminar el abono <strong>{nro}</strong>?
          </p>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setEliminarAbierto(false)}>Cancelar</CButton>
          <CButton color="danger" type="button" onClick={confirmarEliminar}>
            Eliminar
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AbonoViewPage
